import _ from 'lodash'
import unwrapModel from 'scoreboard/components/unwrap-model'
import i18n from 'scoreboard/components/i18n'
import isGeographicOrRollup from './is-geographic-or-rollup'


/**
 * @typedef {Object} KeyDisplayOptions
 * @property {Dataset} model the dataset
 * @property {DatasetField} field the field this result is based on
 * @property {Object} [options.table] the table used to create the result
 * @property {Object} treeItemsById a hash of tree IDs to tree items
 */

/**
 * Handles the "Show Keys" functionality for rollup fields on the Datasets Overview
 *
 * @constructs
 *
 * @param {KeyDisplayOptions} options
 */
function KeyDisplay(options) {
	this.options = options || {}
	this.model = options.model
	this.field = unwrapModel(this.options.field)
	this.underlyingField = unwrapModel(this.model.getUnderlyingField(this.field))
	this.table = unwrapModel(this.options.table)
}

_.extend(KeyDisplay.prototype, {

	/**
	 * Returns whether keys should be displayed based on the showKeys setting
	 * for the field and table
	 */
	isDisplayed: function () {
		if (!isGeographicOrRollup(this.underlyingField.dataType)) {
			return false
		}
		var tableShowKeys = _.result(this.table, 'showKeys')
		if (!_.isNil(tableShowKeys)) {
			return tableShowKeys
		}
		return this.underlyingField.showKeys
	},

	/**
	 * Returns a display String for the keys for a tree item.  For example,
	 * 'W3YBFF, WJJDAA'
	 */
	keys: function (treeId) {
		if (!this.isDisplayed(this.field, this.table)) {
			return ''
		}

		var keySetId = this.underlyingField.keySetId
		var treeItem = this.options.treeItemsById[treeId]
		if (treeItem) {
			var keys = treeItem.keys?.[keySetId]
			return _.join(keys, ', ')
		} else {
			return ''
		}
	},

	/**
	 * Returns a display String for a tree item, which includes its label and
	 * keys.  For example, 'United States Army Forces Command (W3YBFF, WJJDAA)'
	 */
	labelAndKeys: function(treeId) {
		let label = ''

		// #31134 for added records, if the tree item is blank, treeId will be 0
		if (treeId === 0) {
			return label
		}

		const treeItem = this.options.treeItemsById[treeId]
		if (!treeItem) {
			return i18n.get('datasets.trees.item.deleted')
		}

		if (treeItem.label != null) {
			label = treeItem.label
			if (this.isDisplayed()) {
				let keys = this.keys(treeId)
				if (!_.isEmpty(keys)) {
					label += ' (' + keys + ')'
				}
			}
		}

		return label
	}

})

export default KeyDisplay

KeyDisplay.__filename__ = 'datasets/key-display.js'
